import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export type Lang = 'en' | 'ru' | 'kk';

/** Order matters: this drives the switcher in the profile menu. */
export const LANGS: { code: Lang; label: string }[] = [
  { code: 'en', label: 'EN' },
  { code: 'ru', label: 'RU' },
  { code: 'kk', label: 'KZ' },   // ISO code is kk; users know it as KZ
];

export const DEFAULT_LANG: Lang = 'en';

const STORAGE_KEY = 'lang';

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private translate = inject(TranslateService);

  private lang = new BehaviorSubject<Lang>(DEFAULT_LANG);
  lang$ = this.lang.asObservable();

  get current(): Lang {
    return this.lang.value;
  }

  /**
   * Called once from APP_INITIALIZER, long before the WebSocket brings the profile.
   * Guessing from localStorage/the browser here is what keeps the UI from rendering
   * in English and then flipping when USERDATA lands a second later.
   */
  init(): Promise<void> {
    this.translate.setFallbackLang(DEFAULT_LANG);
    const initial = this.readStored() ?? this.fromNavigator() ?? DEFAULT_LANG;
    return this.apply(initial);
  }

  /** User picked a language. Persists locally; the caller pushes it to the profile. */
  set(lang: Lang): Promise<void> {
    if (!this.isSupported(lang)) return Promise.resolve();
    this.store(lang);
    return this.apply(lang);
  }

  /**
   * The language stored in the user's profile, arriving with USERDATA. Deliberately
   * does NOT write back to the server — that would echo the value straight back.
   * A null/unknown value means the user never chose one, so we keep the local guess.
   */
  applyFromServer(lang: string | null | undefined): void {
    if (!lang || !this.isSupported(lang)) return;
    if (lang === this.current) return;
    this.store(lang);
    void this.apply(lang);
  }

  private apply(lang: Lang): Promise<void> {
    return new Promise<void>(resolve => {
      this.translate.use(lang).subscribe({
        next: () => {
          this.lang.next(lang);
          document.documentElement.lang = lang;
          resolve();
        },
        // A missing bundle must not wedge app startup behind a rejected initializer.
        error: () => resolve(),
      });
    });
  }

  private isSupported(lang: string): lang is Lang {
    return LANGS.some(l => l.code === lang);
  }

  private readStored(): Lang | null {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v && this.isSupported(v) ? v : null;
    } catch {
      return null;   // private mode / blocked storage
    }
  }

  private store(lang: Lang): void {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch { /* not fatal — the profile is the durable copy */ }
  }

  /** 'ru-RU' -> 'ru'. Kazakh browsers report 'kk' or 'kk-KZ'. */
  private fromNavigator(): Lang | null {
    const tag = (navigator.language || '').toLowerCase().split('-')[0];
    return this.isSupported(tag) ? tag : null;
  }
}
